// @ts-check
import { gql } from '@apollo/client/core';
import React from 'react';
import BookmarkIcon from '../../components/BookmarkIcon';
import {
  Box,
  Company,
  Flex,
  Page,
  Product,
  QRCode,
  Registration,
  Text,
  Title,
} from '../../pdf-design-system';
import { Image } from '@react-pdf/renderer';
import { PagesProvider } from '../../pdf-design-system/Base/PageLayout/Page';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import gtAmerica from './fonts/gtAmerica';
// import stencil from './images/stencil1.png';
import messages from './locale/messages';
import MaximeHerkuftLogo from './MaximeHerkuftLogo';

const mapping = {
  'MAXIME ORTSWEIN': 'Ortswein',
  'MAXIME LAGENWEIN': 'Lagenwein',
  'MAXIME GUTSWEIN': 'Gutswein',
};

export default function MaximeHerkunftKatalog({ data, locale, ...rest }) {
  const { collection } = data;
  const sections = afterQuery(data, rest);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {sections.map((_, index) => (
          <CatalogPage
            key={index}
            tables={sections.slice(index, index + 1)}
            theme={theme}
            baseUrl={collection?.site?.publicUrl}
            {...rest}
          />
        ))}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

function CatalogPage({ tables, theme, bleed = 3, style, baseUrl, ...rest }) {
  // Only 1 table
  const { registration } = tables[0];
  return (
    <Page
      size={{ width: `${148 + 2 * bleed}mm`, height: `${210 + 2 * bleed}mm` }}
      style={[
        { paddingVertical: `${bleed}mm`, paddingHorizontal: `${bleed}mm` },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {/* <Stencil
        src={stencil2}
        style={{ height: '147mm', padding: `${bleed}mm` }}
      /> */}
      <PageHeader theme={theme} bleed={bleed} />
      <PageBody bleed={bleed}>
        <ExhibitorHeader registration={registration} />
        <Box key={registration.registrationId} style={{ height: '117mm' }}>
          <Registration.Provider value={registration}>
            <Flex direction="column">
              {registration.registeredProducts?.nodes?.map(
                (registeredProduct, index) => (
                  <ProductRow key={index}>
                    <Flex>
                      <Box style={{ width: '100%' }}>
                        <Flex
                          style={{
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Flex>
                            <ProductName
                              registeredProduct={registeredProduct}
                            />
                            {registeredProduct.fassprobe?.value && (
                              <Text variant="light">| Fassprobe</Text>
                            )}
                          </Flex>
                          <MaximeKlassifikation
                            registeredProduct={registeredProduct}
                          />
                        </Flex>
                        <Analytics registeredProduct={registeredProduct} />
                        <Price registeredProduct={registeredProduct} />
                      </Box>
                      <Bookmark registeredProduct={registeredProduct} />
                      <TastingNote registeredProduct={registeredProduct} />
                    </Flex>
                  </ProductRow>
                )
              )}
              <HorizontalDottedLine length={50} />
            </Flex>
          </Registration.Provider>
        </Box>
      </PageBody>
      <QRCodeRow registration={registration} baseUrl={baseUrl} bleed={bleed} />
      <Box
        style={{
          position: 'absolute',
          backgroundColor: theme.colors.accent,
          bottom: 0,
          left: 0,
          right: 0,
          height: `${10 + bleed}mm`,
          paddingBottom: `${bleed}mm`,
          alignItems: 'center',
        }}
      />
    </Page>
  );
}
function HorizontalDottedLine({ length }) {
  return <Text style={{ marginBottom: '3mm' }}>{'.   '.repeat(length)}</Text>;
}

function TastingNote({ registeredProduct }) {
  const { tastingNote } = registeredProduct;
  if (!tastingNote) return null;
  return (
    <Box
      style={{
        marginBottom: 6,
        position: 'absolute',
        right: 0,
        bottom: 10,
        top: 13,
        textAlign: 'right',
        width: '7cm',
        height: '1cm',
      }}
    >
      {tastingNote?.rating && (
        <Text variant="light">Mein Rating: {tastingNote?.rating}/5</Text>
      )}
      <Text variant="light">{tastingNote?.note}</Text>
    </Box>
  );
}

function ProductRow({ children }) {
  return (
    <Box style={{ height: '20mm', flexShrink: 0 }}>
      <HorizontalDottedLine length={50} />
      {children}
    </Box>
  );
}

function PageHeader({ bleed, theme }) {
  return (
    <Flex
      style={{
        position: 'absolute',
        backgroundColor: theme.colors.primary,
        top: 0,
        left: 0,
        right: 0,
        height: `${24.75 + bleed}mm`,
        paddingTop: `${bleed}mm`,
        alignItems: 'flex-end',
      }}
    >
      <MaximeHerkuftLogo
        color="white"
        style={{ height: '14.03mm', marginBottom: '5mm' }}
      />
    </Flex>
  );
}

function PageBody({ bleed, children }) {
  return (
    <Box
      style={{
        paddingHorizontal: `${15 + bleed}mm`,
        paddingTop: `${28 + bleed}mm`,
        paddingBottom: '10mm',
        // height: '216mm',
        position: 'relative',
      }}
    >
      {children}
    </Box>
  );
}

function QRCodeRow({ registration, baseUrl, bleed }) {
  return (
    <Flex
      style={{
        justifyContent: 'center',
        alignItems: 'flex-end',
        position: 'absolute',
        bottom: `${14 + bleed}mm`,
        left: 0,
        right: 0,
        paddingHorizontal: `${17 + bleed}mm`,
      }}
    >
      {/* <Box style={{width: '50%'}}/> */}
      <QRCode
        url={`${baseUrl}/e/${registration.companyId}`}
        errorCorrectionLevel="L"
        margin={0}
        style={{
          width: '15mm',
          height: '15mm',
          flexShrink: 0,

          marginTop: 10,
          marginBottom: 2,
          marginRight: '2mm',
          marginLeft: '20mm',
        }}
      />
      <Box>
        <Text variant="light">Scannen für</Text>
        <Text variant="light">persönliche Notizen</Text>
      </Box>
    </Flex>
  );
}

/**
 * @param {number} number
 */
function formatPrice(number) {
  if (number == null) return '';
  return number.toFixed(2).replace('.', ',');
}

/**
 * @param {number} number
 */
function formatNumber(number) {
  if (number == null) return '';
  return number.toFixed(1).replace('.', ',');
}

function ExhibitorHeader({ registration }) {
  return (
    <Flex style={{ justifyContent: 'space-between' }}>
      <Box style={{ width: '50%' }}>
        <Company.ProfileLogo
          companyProfile={{
            logo: registration.profile.autoSizedLogo,
          }}
          height={50}
          width={100}
          gravity={'north_west'}
        />
      </Box>
      <Flex
        style={{
          flexDirection: 'column',
          alignItems: 'flex-end',
          marginBottom: '2mm',
          width: '50%',
        }}
      >
        <Text>{registration?.stand?.name || 'Tisch 34'}</Text>
        <Title style={{ marginBottom: 3, textAlign: 'right' }}>
          {registration.profile.name}
        </Title>
        <Text variant="light">{registration?.profile?.website}</Text>
        <Text variant="light">{registration?.profile?.email}</Text>
      </Flex>
    </Flex>
  );
}

function MaximeKlassifikation({ registeredProduct, variant = 'light' }) {
  const maximeKlassification =
    registeredProduct?.maximeKlassifikation?.value?.[0] &&
    registeredProduct?.maximeKlassifikation?.value?.[0] !==
      'Nicht klassifiziert'
      ? registeredProduct?.maximeKlassifikation?.value?.[0]
      : ' ';
  return <Text variant={variant}>{mapping[maximeKlassification]}</Text>;
}

function ProductName({ registeredProduct }) {
  return (
    <Text style={{ marginRight: '1mm' }}>
      {registeredProduct.product?.name}
    </Text>
  );
}

function Analytics({ registeredProduct, variant = 'light' }) {
  const { product } = registeredProduct;
  const analyticsString = [
    product.designation,
    product.alcohol && `Alk. ${formatNumber(product.alcohol)}%`,
    product.residualSugar && `RS ${formatNumber(product.residualSugar)} g/l`,
    product.totalAcidity && `GS ${formatNumber(product.totalAcidity)} g/l`,
  ]
    .filter(Boolean)
    .join(' | ');
  return <Text variant={variant}>{analyticsString}</Text>;
}

function Price({ registeredProduct, variant = 'light' }) {
  if (registeredProduct.pricing?.price !== null)
    return (
      <Text variant={variant}>
        {registeredProduct.pricing?.currency}{' '}
        {formatPrice(registeredProduct.pricing?.price)}{' '}
        {registeredProduct.pricing?.priceType}
      </Text>
    );
  return <Text variant={variant}>Preis auf Anfrage</Text>;
}

function Bookmark({ registeredProduct }) {
  const { bookmark } = registeredProduct;
  if (!bookmark) return null;
  return (
    <Box
      style={{
        marginBottom: 6,
        marginLeft: -20,
        position: 'absolute',
        top: -0.5,
      }}
    >
      <BookmarkIcon variant="filled" color={theme.colors.primary} />
    </Box>
  );
}

/**
 * @param {ReturnType<afterQuery>[number]} section
 */
function getTable(section) {
  if (section.__typename === 'RegistrationCatalogSection') {
    return section.registration;
  }
  return undefined;
}

/** @typedef {ReturnType<getTable>} Table */

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

const colors = {
  primary: '#009e92',
  accent: '#593a23',
};

/** @type {Theme} */
export const theme = {
  colors,
  components: {
    Text: {
      base: { ...gtAmerica, fontSize: '10.5pt' },
      // default: { fontSize: '10.5pt' },
      light: { color: '#888' },
    },
    Title: {
      base: { ...gtAmerica, textTransform: 'uppercase' },
      small: {
        fontSize: 8,
        lineHeight: 1,
      },
      default: {
        fontSize: '14pt',
        lineHeight: 0.95,
      },
      large: {
        fontSize: 18,
        lineHeight: 1.3,
      },
    },
  },
};

MaximeHerkunftKatalog.fragment = gql`
  fragment MaximeHerkunftKatalog on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...MaximeHerkunftKatalog_Table
    }
  }

  fragment MaximeHerkunftKatalog_Table on RegistrationCatalogSection {
    registration {
      registrationId
      name
      companyId
      stand {
        name
      }

      profile {
        name
        street
        postalCode
        city
        country
        telephone
        email
        website
        shortDescription
        ...ProfileLogo
        autoSizedLogo: logo {
          url
        }
      }

      registeredProducts {
        nodes {
          maximeKlassifikation: customFieldValue(key: "maximeKlassifikation") {
            ... on MultiSelectFieldValue {
              value
            }
          }
          fassprobe: customFieldValue(key: "fassprobe") {
            ... on YesNoFieldValue {
              value
            }
          }
          producer {
            isSeekingDistribution
            distributionContacts {
              contactId
              companyName
            }
          }
          pricing {
            currency
            priceType
            price
          }
          productId
          product {
            productId
            name
            alcohol

            regionHierarchy
            denomination
            ...Blend
            ...BottleImage
            ... on Wine {
              vineyard
              designation
              residualSugar
              totalAcidity
            }
          }
        }
      }
    }
  }
  ${Product.Blend.fragment}
  ${Product.BottleImage.fragment}
  ${Company.ProfileLogo.fragment}
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}
